/* eslint-disable react-hooks/exhaustive-deps */
import moment from 'moment';
import { Area } from '@antv/g2plot';
import styled from 'styled-components';
import { useLayoutEffect, useMemo, useState } from 'react';
import { useGeneralContext } from '../../../../context/General';

export const Style = styled.div`
  .per-time-tooltip {
    min-width: 230px;
    padding: var(--size) 12px;
    display: flex;
    flex-direction: column;

    .label {
      font-size: 14px;
      font-weight: 700;
      margin-bottom: 12px;
      color: #001529;
    }

    .value {
      display: flex;

      label {
        margin-bottom: 0px;
        font-size: 12px;
      }

      .title {
        font-weight: 500;
        width: 140px;
      }

      .percentage {
        color: var(--color-primary-70);
        font-weight: 700;
      }
    }
  }
`;

const CustomTooltipContent = (data, t, isConsolidated) => {
  if (data[0]) {
    if (data[0].data) {
      const { label, value, date } = data[0].data;

      return `
        <div class="per-time-tooltip">
          <label class="label">${label}</label>
          <label class="second-label">${moment(date).format('D MMM YYYY')}</label>
          <div class="value mb-1">
            <label class="title">${t(isConsolidated ? 'Consolidated Progress' : 'Dashboard::Progress')}</label>
            <label class="percentage">${value}%</label>
          </div>
        </div>
      `;
    }
  }
  return '';
};

const getFillColor = (type, theme) => {
  switch (type) {
    case 'SCP_PROJECT':
      // return 'l(270) 0:#ffff 1:var(--mmt-primary-70)';
      return theme.mmtPrimary6;
    default:
      return theme?.mmtPrimary7;
  }
}

const addVirtualPoints = ({
  startValue = 0, values, initValue, lastValue, startFromZero = true,
}) => {
  const items = [];
  if (startFromZero) {
    const incrVal = (lastValue - initValue) / values;
    for (let i = 0; i < values; i++) {
      items.push({
        time: `_${startValue}`,
        dontShow: true,
        label: '',
        value: 0 + (incrVal * i),
        sources: 0,
      });
      startValue++;
    }
    return items;
  } else {
    const incrVal = (lastValue - initValue) / (values + 1);
    for (let i = 0; i < values; i++) {
      items.push({
        time: `_${startValue}`,
        dontShow: true,
        label: '',
        value: initValue + (incrVal * (i + 1)),
        sources: 0,
      });
      startValue++;
    }
  }

  return items;
}

const oneCase = ({ publishments }) => {
  const items = []
  const item = publishments[0];
  const before = addVirtualPoints({
    startValue: 0,
    values: 50,
    lastValue: item.progress || 0,
    initValue: 0,
  });
  const after = addVirtualPoints({
    startValue: 50,
    values: 50,
    lastValue: item.progress || 0,
    initValue: item.progress || 0,
    startFromZero: false,
  });
  items.push(...before);
  items.push({
    time: '0',
    label: item.label,
    value: item.progress || 0,
    date: item.date,
    sources: item.sources || 0,
  });
  items.push(...after);
  return items;
}

const middleCase = ({ publishments, value }) => {
  const items = [];
  const firstItem = publishments[0];
  const before = addVirtualPoints({
    startValue: 0,
    values: value,
    lastValue: firstItem.progress || 0,
    initValue: 0,
  });
  items.push(...before);
  for (let i = 0; i < publishments.length; i++) {
    const item = publishments[i];
    items.push({
      time: i.toString(),
      label: item.label,
      value: item.progress || 0,
      date: item.date,
      sources: item.sources || 0,
    });
    let toPush = [];
    if (i === publishments.length - 1) {
      toPush = addVirtualPoints({
        startValue: value + (i * value),
        values: value,
        lastValue: item.progress || 0,
        initValue: item.progress || 0,
        startFromZero: false,
      });
    } else {
      toPush = addVirtualPoints({
        startValue: value + (i * value),
        values: value,
        lastValue: publishments[i + 1].progress || 0,
        initValue: item.progress || 0,
        startFromZero: false,
      });
    }
    items.push(...toPush);
  }
  return items;
};

export const useChartHelper = (
  { ref, publishments = [], customYAxis = null, isConsolidated = false, type = 'default' },
  t = (s) => s,
) => {
  const [cont, setCont] = useState(null);
  const { theme } = useGeneralContext();

  const data = useMemo(() => {
    if (publishments.length) {
      switch (publishments.length) {
        case 1:
          return oneCase({ publishments });
        case 2:
        case 3:
          return middleCase({ publishments, value: 25 });
        case 4:
        case 5:
          return middleCase({ publishments, value: 18 });
        default:
          const value = Math.round(50 / publishments.length);
          return middleCase({ publishments, value: value <= 0 ? 0 : value });
      }
    }
    return [];
  }, [publishments]);

  const createChart = () => {
    if (cont) {
      return;
    }

    const area = new Area(ref.current, {
      autoFit: true,
      data,
      xField: 'time',
      yField: 'value',
      animation: false,
      xAxis: {
        label: {
          formatter: () => '',
        },
        nice: true,
        tickLine: {
          style: { opacity: 0 },
        },
      },
      startOnZero: true,
      yAxis: {
        tickMethod: () => {
          return customYAxis || [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
        },
        label: {
          formatter: (val) => `${val}%`,
        },
        nice: true,
      },
      tooltip: {
        customContent: (a, d) => {
          if (a && typeof a === 'string') {
            if (a.indexOf('_') === -1) {
              return CustomTooltipContent(d, t, isConsolidated);
            }
          }
          return '';
        },
        showCrosshairs: false,
      },
      point: {
        size: 3,
        shape: 'circle',
        style: (a) => {
          return {
            fill: (a.time.indexOf('_') !== -1) ? 'transparent' : 'white',
            stroke: (a.time.indexOf('_') !== -1) ? 'transparent' : theme?.mmtPrimary7,
          }
        },
      },
      areaStyle: () => ({
        fill: getFillColor(type, theme),
      }),
    });
    area.render();
    setCont(area);
  };

  useLayoutEffect(() => {
    if (cont) {
      cont.update({
        data,
        startOnZero: true,
        tooltip: {
          customContent: (a, data) => {
            if (a && typeof a === 'string') {
              if (a.indexOf('_') === -1) {
                return CustomTooltipContent(data, t, isConsolidated);
              }
            }
            return '';
          }
        },
      });
    } else {
      createChart();
    }
  }, [data, t]);
};
